import {
  ReportSiteAction,
  ReportSiteReason,
  WebEvent,
  ScreenTimeEvent,
} from '../types/api/events';
import { buildURL } from '../helper/urlUtils';
import { isLoggedInWithDelegation } from '../delegation/delegation';
import { getReportingStore } from '../storage/stores';
import { getDateTime, getISOfromDateTime, toSeconds } from '../lib/dateTime';
import { isReportableYoutubeEvent } from '../navigation/youtubeNavigation';
import { getNavigatorUserAgent } from '../chrome/wrappers';

const PENDING_QUEUE_EVENTS_ONE_POST_LIMIT = 20;
const WEB_DOMAIN = 101;

// Subtypes
const YOUTUBE_WEB_SUBTYPE = 151;

/**
 *
 * @param url - event url
 * @returns a Block event of type Safety Net for the provided URL
 */
export const buildBlockedBySafetyNetEvent = (url: string): WebEvent => {
  const SAFETY_NET_CATEGORY_ID = 40;
  return {
    url,
    hostname: buildURL(url)?.hostname ?? '',
    categories: [SAFETY_NET_CATEGORY_ID],
    categories_match: [SAFETY_NET_CATEGORY_ID],
    action: ReportSiteAction.BLOCK,
    reason: ReportSiteReason.CATEGORY_BLOCK,
  };
};

/**
 *
 * @param event - web event
 * @param timestamp - detection timestamp
 * @returns returns the list of navigation events to be reported
 */
export const buildNavigation = (event: WebEvent, timestamp: number) => {
  return [
    {
      url: event.url,
      host: event.hostname,
      categories: event.categories,
      categories_match: event.categories_match,
      timestamp,
      action: event.action,
      reason: event.reason,
      alert: false,
      type: WEB_DOMAIN,
      client_technology: 'chromebook_api',
      user_agent: getNavigatorUserAgent() ?? '',
      // Append extra info for YouTube videos.
      ...(isReportableYoutubeEvent(event.url) && {
        subtype: YOUTUBE_WEB_SUBTYPE,
        video_title: event.video_title ?? '',
        video_channel_title: event.video_channel_title ?? '',
        video_duration: event.video_duration ?? 0,
      }),
    },
  ];
};

/**
 *
 * @param timestamp - detection timestamp
 * @returns the pending screen time events (up to PENDING_QUEUE_EVENTS_ONE_POST_LIMIT) if delegated, or else a single event of 0 seconds
 */
export const buildScreenTime = (timestamp: number): ScreenTimeEvent[] => {
  // We have to fetch the screen time events if the device is logged in with delegation, even if the delegation is stopped,
  // as they will only be in the queue if the time has been consumed while the device was delegated.
  if (isLoggedInWithDelegation()) {
    const events = buildScreenTimeWithDelegation();
    if (events.length > 0) {
      return events;
    }
  }

  return [{ ts: timestamp, seconds: 0 }];
};

/**
 * Gets the events to report for Screen Time with delegation.
 *
 * @returns the pending screen time events (up to PENDING_QUEUE_EVENTS_ONE_POST_LIMIT)
 */
export const buildScreenTimeWithDelegation = (): ScreenTimeEvent[] => {
  return getReportingStore()
    .getScreenTimeEvents()
    .slice(0, PENDING_QUEUE_EVENTS_ONE_POST_LIMIT);
};

/**
 *
 * @param startTimestamp - start timestamp slot
 * @param timeInSeconds - slot seconds consumed
 *
 * @remarks add new event to the pending reporting queue
 */
export const addScreenTimeReport = (
  startTimestamp: number,
  timeInSeconds: number
) => {
  console.debug(
    `Screen Time slot enqueued for reporting: startTimestamp = ${getISOfromDateTime(
      getDateTime(startTimestamp)
    )}, timeInSeconds = ${timeInSeconds}.`
  );

  getReportingStore().setScreenTimeEvents([
    ...getReportingStore().getScreenTimeEvents(),
    {
      ts: toSeconds(startTimestamp), // Seconds precision
      seconds: timeInSeconds,
    },
  ]);
};

/**
 *
 * @param itemsToRemove - events reported correcty that should be removed from the reporting queue
 */
export const removeScreenTimeReport = (itemsToRemove: ScreenTimeEvent[]) => {
  getReportingStore().setScreenTimeEvents(
    getReportingStore()
      .getScreenTimeEvents()
      .filter(
        ar =>
          !itemsToRemove.find(
            rm => rm.ts === ar.ts && ar.seconds === rm.seconds
          )
      )
  );
};
